/**
 *
 * LoginForm
 *
 */

import React, { useState, useEffect, useCallback } from 'react'

// NPM Libraries
import axios from 'axios'
import * as Sentry from '@sentry/react'
import { useLocation, useHistory } from 'react-router-dom'
import { useMergeState, useDebounce, usePasswordless } from 'hooks'
import { useMobileDetect } from '@joeyparis/hooks'
import FloatingLabel from '@joeyparis/react-floating-label-input'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Graphql
import client from 'configureApolloClient'

// Components
import { queryVariables } from 'components/utils'

const LoginFormActions = {
	LOGIN: 0,
	FORGOT_PASSWORD: 1,
	UNCONFIRMED: 2,
	PASSWORD_RESET: 3,
}

function LoginForm() {
	const location = useLocation()
	const history = useHistory()
	const { reset_password_token } = queryVariables(location)

	const [flash, setFlash] = useState([])
	const [warden_message, setWardenMessage] = useState()
	const [action, setAction] = useState(LoginFormActions.LOGIN)
	const [alert, setAlert] = useState()
	const [notice, setNotice] = useState()
	const [password_errors, setPasswordErrors] = useState([])

	useEffect(() => {
		setWardenMessage((flash.find((f) => f[0] === 'warden_message') || {})[1])

		setAlert((flash.find((f) => f[0] === 'alert') || {})[1])

		const notice_message = (flash.find((f) => f[0] === 'notice') || {})[1]
		setNotice(notice_message === 'Signed out successfully.' ? '' : notice_message)
	}, [flash])

	useEffect(() => {
		if (warden_message === 'unconfirmed') {
			setAction(LoginFormActions.UNCONFIRMED)
		}
	}, [warden_message])

	useEffect(() => {
		if (reset_password_token) {
			setAction(LoginFormActions.PASSWORD_RESET)
		}
	}, [reset_password_token, action])

	const [form, setForm] = useMergeState({
		email: sessionStorage.getItem('login_email') || '',
		password: '',
		password_confirmation: '',
		remember_me: false,
	})

	const [submitting, setSubmitting] = useState(false)
	const [login_success, setLoginSuccess] = useState(false)
	const [one_touch_approval_request, setOneTouchApprovalRequest] = useState(false)
	const [token_sent_to, setTokenSentTo] = useState()

	const { login: passwordlessLoginRequest, browser_supported } = usePasswordless()

	const { LOGIN, FORGOT_PASSWORD, UNCONFIRMED, PASSWORD_RESET } = LoginFormActions

	const is_login = LOGIN === action
	const is_forgot_password = FORGOT_PASSWORD === action
	const is_unconfirmed = UNCONFIRMED === action
	const is_password_reset = PASSWORD_RESET === action

	let path
	let submit_button
	let show_password
	if (is_unconfirmed) {
		path = '/users/confirmation'
		submit_button = 'Resend my confirmation email'
		show_password = false
	} else if (is_forgot_password) {
		path = '/users/password'
		submit_button = 'Send me reset password instructions'
		show_password = false
	} else if (is_login) {
		path = '/users/sign_in'
		submit_button = 'Login'
		show_password = true
	} else if (is_password_reset) {
		path = '/users/password'
		submit_button = 'Change my password'
		show_password = true
	}

	const password_matches = is_password_reset ? form.password === form.password_confirmation : true

	const checkPasswordStrength = (new_password) => {
		axios({
			method: 'GET',
			url: `${process.env.API_URL}/users/check_password_strength`,
			params: {
				reset_password_token,
				new_password,
			},
		}).then(({ data }) => {
			setPasswordErrors(data)
		})
	}

	const [checkPasswordStrengthDebounced] = useDebounce(checkPasswordStrength, 500)

	useEffect(() => {
		if (is_password_reset && reset_password_token && form.password) {
			checkPasswordStrengthDebounced(form.password)
		}

		if (!form.password) {
			setPasswordErrors([])
		}
	}, [is_password_reset, reset_password_token, form.password, checkPasswordStrengthDebounced])

	const passwordlessLogin = useCallback(async (token) => {
		setAlert('')
		setSubmitting(true)

		const { status, headers } = await axios({
			method: 'POST',
			url: process.env.API_URL + '/users/sign_in_passwordless',
			data: {
				passwordless_token: token,
			},
		})

		const error_headers = headers['x-flash']

		setSubmitting(false)
		if (status === 200 && !error_headers) {
			if (is_login) {
				setLoginSuccess(true)
				client.resetStore()
			}

			if (is_password_reset) {
				setLoginSuccess(true)
				client.resetStore()
			}
		}
	})

	const onSubmit = async (e) => {
		e.preventDefault()

		if (submitting) return false
		if (is_password_reset && password_errors > 0) return false
		if (form.email) sessionStorage.setItem('login_email', form.email)

		setAlert('')
		setSubmitting(true)

		try {
			const { status, headers } = await axios({
				method: is_password_reset ? 'PUT' : 'POST',
				url: process.env.API_URL + path,
				data: {
					user: {
						email: form.email,
						password: form.password,
						password_confirmation: form.password_confirmation,
						reset_password_token,
						remember_me: form.remember_me,
					},
				},
			})

			if (!headers['x-authy']) {
				setForm({ password: '', password_confirmation: '' })
			}

			const error_headers = headers['x-flash']

			setSubmitting(false)
			if (status === 200 && !error_headers) {
				if (is_login) {
					setLoginSuccess(true)
					client.resetStore()
				}

				if (is_password_reset) {
					setLoginSuccess(true)
					client.resetStore()
				}
			}

			if (error_headers) {
				setFlash(JSON.parse(error_headers))
			}

			if (headers['x-authy'] === 'approval_request') {
				setAlert(false)
				setOneTouchApprovalRequest(true)
			}

			if (headers['x-authy'] === 'token_sent') {
				setAlert(false)
				setTokenSentTo(headers['x-authy-cellphone'])
			}

			setSubmitting(false)
		} catch (err) {

			setSubmitting(false)
					
			if (is_password_reset) {
				if (err.response.status === 404) {
					setLoginSuccess(true)
					client.resetStore()
					setTimeout(() => {
						history.push('/')
					}, 100)
				} else if (err.response.status === 400) {
					setPasswordErrors(err.response.data)
				}
			} else {
				console.error(err) // eslint-disable-line no-console
				Sentry.captureException(err) // eslint-disable-line no-undef
			}
		}

		return false
	}

	const checkOneTouchApprovalStatus = () => {
		axios({
			method: 'GET',
			url: `${process.env.API_URL}/users/authy_approval_request_status`,
		}).then(({ data }) => {
			if (data.status === 'approved') {
				onSubmit({ preventDefault: () => {} })
			} else if (data.status === 'denied') {
				setOneTouchApprovalRequest(false)
				setAlert('Login Approval Denied')
			}
		})
	}

	useEffect(() => {
		if (one_touch_approval_request) {
			const oneTouchApprovalStatusCheck = setInterval(checkOneTouchApprovalStatus, 1000)
			return () => {
				clearInterval(oneTouchApprovalStatusCheck)
			}
		}

		return () => {}
	}, [one_touch_approval_request])

	const { is_mobile } = useMobileDetect()

	if (login_success) {
		return (
			<div className="row">
				<div className="col-12">
					<div className="mx-auto" style={{ maxWidth: 420 }}>
						<p className="text-center text-success">
							{is_login && 'Login successful!'}
							{is_password_reset && 'Password reset successfully!'}
						</p>
						<p className="text-danger">{alert}</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="row">
			<div className="col-12">
				<div className="mx-auto" style={{ maxWidth: 420 }}>
					<p className="text-center" style={{ color: '#4D4F5C' }}>
						Welcome back! Please login to your account.
					</p>
					<form onSubmit={onSubmit} action={process.env.API_URL + path} method="post">
						{!one_touch_approval_request && (
							<React.Fragment>
								{!is_password_reset && (
									<FloatingLabel
										id="email"
										label="email"
										type="email"
										name="user[email]"
										value={form.email}
										onChange={(e) => setForm({ email: e.target.value })}
										autoFocus
										required
										disabled={submitting}
									/>
								)}

								{show_password && (
									<Tippy
										theme="light-border"
										placement="right"
										arrow
										duration={[null, 0]}
										visible={password_errors.length > 0}
										content={
											<div className="text-left">
												{password_errors.map((v, i) => (
													<div key={i}>Your password {v}</div>
												))}
											</div>
										}
									>
										<span>
											<FloatingLabel
												id="password"
												label="password"
												type="password"
												name="user[password]"
												value={form.password}
												onChange={(e) => setForm({ password: e.target.value })}
												containerStyle={{
													display: show_password ? 'flex' : 'none',
												}}
												disabled={submitting}
												required
											/>
										</span>
									</Tippy>
								)}

								{is_password_reset && (
									<FloatingLabel
										id="password_confirmation"
										label="Password Confirmation"
										type="password"
										name="user[password_confirmation]"
										value={form.password_confirmation}
										onChange={(e) => setForm({ password_confirmation: e.target.value })}
										disabled={submitting}
										required
										invalid={!password_matches && Boolean(form.password_confirmation)}
										message={
											!password_matches && Boolean(form.password_confirmation)
												? 'Passwords do not match'
												: ''
										}
									/>
								)}

								<div className="row mt-4">
									<div className="col-6">
										<div
											className="custom-control custom-checkbox"
											style={{ opacity: show_password && !is_password_reset ? 1 : 0 }}
										>
											<input
												type="checkbox"
												className="custom-control-input"
												id="remember-me"
												checked={form.remember_me}
												onChange={(e) => setForm({ remember_me: e.target.checked })}
												disabled={!show_password || is_password_reset || submitting}
											/>
											<label className="custom-control-label" htmlFor="remember-me">
												Remember me
											</label>
										</div>
									</div>
									{!is_password_reset && (
										<React.Fragment>
											<div className="col-6 text-right">
												<button
													type="button"
													className="btn-unstyled"
													onClick={() => setAction(is_login ? FORGOT_PASSWORD : LOGIN)}
												>
													{is_login ? 'Forgot Password' : 'Login'}
												</button>
											</div>
										</React.Fragment>
									)}
								</div>

								<div className="row">
									<div className={show_password && !is_password_reset ? 'col-12' : 'col-12'}>
										<button
											type="submit"
											className="btn btn-lg btn-block btn-primary mt-5 text-center"
											disabled={submitting}
										>
											{submit_button}
										</button>
									</div>
									{is_login && browser_supported && (
										<div className="col-12">
											<button
												type="button"
												className="btn btn-lg btn-block btn-outline-primary mt-5 text-center"
												disabled={submitting}
												onClick={async () => {
													const { token, erorr } = await passwordlessLoginRequest()
													passwordlessLogin(token)
												}}
											>
												{is_mobile && (
													<React.Fragment>
														<FontAwesomeIcon icon="fingerprint" className="mr-2" />
														Use Face or Fingerprint ID
													</React.Fragment>
												)}
												{!is_mobile && (
													<React.Fragment>
														<FontAwesomeIcon icon="lock" className="mr-2" />
														<span>Use Passwordless Login</span>
													</React.Fragment>
												)}
											</button>
										</div>
									)}
								</div>
							</React.Fragment>
						)}

						{one_touch_approval_request && (
							<p className="text-center">
								An approval request has been sent to your smart phone. You will automatically be signed
								in after approval.
							</p>
						)}

						{(alert || notice) && (
							<div className="row mt-3">
								<div className="col-12">
									{alert && !login_success && <p className="text-danger mb-0">{alert}</p>}
									{notice && notice !== 'Signed out successfully.' && (
										<p className="text-success mb-0">{notice}</p>
									)}
									{!is_unconfirmed && warden_message === 'unconfirmed' && (
										<button
											type="button"
											className="btn-unstyled"
											onClick={() => setAction(UNCONFIRMED)}
										>
											Didn&apos;t receive confirmation email?
										</button>
									)}
								</div>
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	)
}

LoginForm.propTypes = {}

LoginForm.defaultProps = {}

export default LoginForm
